import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { ATTEMPT_SUMMARY_TYPE } from '@cont/Quiz/constants';
import useOngoingSection from '@cont/Quiz/Taking/Common/useOngoingSection';
import { getIsPendingAction } from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { NTA_COLORS, NTA_PAGE_PADDING_X } from '../constants';
import {
  ntaBodyBoldCss,
  ntaButtonCss,
  ntaIntroCss,
  ntaPageCss
} from '../typography';
import useNtaSectionSummaries from './useNtaSectionSummaries';
import { useNtaExamSummary } from './NtaExamSummaryContext';

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px ${NTA_PAGE_PADDING_X} 24px;
  box-sizing: border-box;
`;

const Inner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  margin: 0;
  padding-bottom: 16px;
  text-align: center;
  color: ${NTA_COLORS.textPrimary};
  ${ntaIntroCss}
  font-size: 24px;
  line-height: 24px;
  letter-spacing: 0;
`;

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  width: 100%;
`;

const TableWrap = styled.div`
  border: 1px solid ${NTA_COLORS.tableBorder};
  width: 100%;
  padding: 4px 0;
  box-sizing: border-box;
  overflow-x: auto;
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns:
    minmax(160px, 1.5fr) minmax(100px, 0.9fr) minmax(72px, 0.55fr)
    minmax(88px, 0.7fr) minmax(100px, 0.8fr) minmax(180px, 2.2fr)
    minmax(72px, 0.6fr);
  width: 100%;
  min-width: 900px;
`;

const HeaderRow = styled.div`
  display: contents;
`;

const BodyRow = styled.div`
  display: contents;
`;

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 12px;
  box-sizing: border-box;
  background: ${NTA_COLORS.white};
  border-bottom: 1px solid ${NTA_COLORS.tableBorder};
  border-right: 1px solid ${NTA_COLORS.tableBorder};
  color: ${NTA_COLORS.textPrimary};
  ${ntaPageCss}
  ${ntaBodyBoldCss}
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0;
  text-align: center;
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }
`;

const BodyCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 12px;
  box-sizing: border-box;
  background: ${NTA_COLORS.white};
  border-bottom: ${({ $hideBottomBorder }) =>
    $hideBottomBorder ? 'none' : `1px solid ${NTA_COLORS.tableBorder}`};
  border-right: 1px solid ${NTA_COLORS.tableBorder};
  color: ${NTA_COLORS.tableCellText};
  ${ntaPageCss}
  font-size: 14px;
  line-height: 24px;
  text-align: center;

  &:last-child {
    border-right: none;
  }
`;

const SectionNameHeaderCell = styled(HeaderCell)`
  white-space: normal;
  text-align: left;
  justify-content: flex-start;
`;

const SectionNameCell = styled(BodyCell)`
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-align: left;
  justify-content: flex-start;
  align-items: center;
`;

const NumericCell = styled(BodyCell)`
  white-space: nowrap;
`;

const ConfirmBlock = styled.div`
  width: 100%;
  max-width: 921px;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ConfirmText = styled.div`
  text-align: center;
  color: ${NTA_COLORS.textPrimary};
  ${ntaIntroCss}
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0;

  p {
    margin: 0;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionButton = styled.button`
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${NTA_COLORS.confirmButtonBorder};
  background: ${NTA_COLORS.white};
  color: ${NTA_COLORS.textPrimary};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  min-width: ${({ $width }) => $width};
  ${ntaButtonCss}

  &:focus-visible {
    outline: 2px solid ${NTA_COLORS.accentBlue};
    outline-offset: 2px;
  }
`;

const TABLE_COLUMNS = [
  { key: 'sectionName', header: 'Section Name', variant: 'section' },
  { key: 'totalQuestions', header: 'No. of Questions' },
  { key: 'answered', header: 'Answered' },
  { key: 'notAnswered', header: 'Not Answered' },
  { key: 'markedForReview', header: 'Marked for Review' },
  {
    key: 'answeredMarked',
    header: 'Answered & Marked for Review (will be considered for evaluation)'
  },
  { key: 'notVisited', header: 'Not visited' }
];

const NtaExamSummaryPage = ({
  onProceedToConfirm,
  onSubmitSection,
  onTimeoutProceed
}) => {
  const { closeSummary, summaryType, isTimeoutSummary } =
    useNtaExamSummary() ?? {};
  const sectionRows = useNtaSectionSummaries();
  const { onGoingSectionUID } = useOngoingSection();
  const isPendingAction = useSelectorWithShallowEqual(getIsPendingAction);

  const isSectionSummary = summaryType === ATTEMPT_SUMMARY_TYPE.SECTION;
  const visibleRows = isSectionSummary
    ? sectionRows.filter((row) => row.sectionUID === onGoingSectionUID)
    : sectionRows;

  const handleNo = () => {
    closeSummary();
  };

  const handleYes = async () => {
    if (isPendingAction) return;

    if (isTimeoutSummary) {
      onTimeoutProceed?.();
      return;
    }

    closeSummary();

    if (isSectionSummary) {
      await onSubmitSection?.();
      return;
    }

    onProceedToConfirm?.();
  };

  let confirmText = (
    <>
      <p>Are you sure you want to submit for final marking?</p>
      <p>No changes will be allowed after submission.</p>
    </>
  );

  if (isTimeoutSummary) {
    confirmText = (
      <>
        <p>Time is up. Your responses have been recorded.</p>
        <p>Please proceed to view your result.</p>
      </>
    );
  } else if (isSectionSummary) {
    confirmText = (
      <>
        <p>Are you sure you want to submit this section?</p>
        <p>No changes will be allowed for this section after submission.</p>
      </>
    );
  }

  return (
    <Content>
      <Inner>
        <Title>{isSectionSummary ? 'Section Summary' : 'Exam Summary'}</Title>
        <SummarySection>
          <TableWrap>
            <TableGrid
              role="table"
              aria-label={
                isSectionSummary ? 'Section summary' : 'Exam summary by section'
              }
            >
              <HeaderRow role="row">
                {TABLE_COLUMNS.map(({ key, header, variant }) => {
                  const Header =
                    variant === 'section' ? SectionNameHeaderCell : HeaderCell;

                  return (
                    <Header key={key} role="columnheader">
                      {header}
                    </Header>
                  );
                })}
              </HeaderRow>
              {visibleRows.map((row, index) => {
                const isLastRow = index === visibleRows.length - 1;

                return (
                  <BodyRow key={row.sectionUID} role="row">
                    {TABLE_COLUMNS.map(({ key, variant }) => {
                      const Cell =
                        variant === 'section' ? SectionNameCell : NumericCell;

                      return (
                        <Cell
                          key={key}
                          role="cell"
                          $hideBottomBorder={isLastRow}
                        >
                          {row[key]}
                        </Cell>
                      );
                    })}
                  </BodyRow>
                );
              })}
            </TableGrid>
          </TableWrap>
          <ConfirmBlock>
            <ConfirmText>{confirmText}</ConfirmText>
            <ButtonRow>
              <ActionButton
                type="button"
                $width={isTimeoutSummary ? '160px' : '105px'}
                $disabled={isPendingAction}
                disabled={isPendingAction}
                onClick={handleYes}
              >
                {isTimeoutSummary ? 'VIEW RESULT' : 'YES'}
              </ActionButton>
              {!isTimeoutSummary ? (
                <ActionButton type="button" $width="60px" onClick={handleNo}>
                  NO
                </ActionButton>
              ) : null}
            </ButtonRow>
          </ConfirmBlock>
        </SummarySection>
      </Inner>
    </Content>
  );
};

NtaExamSummaryPage.propTypes = {
  onProceedToConfirm: PropTypes.func,
  onSubmitSection: PropTypes.func,
  onTimeoutProceed: PropTypes.func
};

export default NtaExamSummaryPage;
